import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import BotonVolver from "../../../components/BotonVolver";

// Module-level map to deduplicate identical seguimiento fetches across
// possible double-mounts (React StrictMode) or concurrent triggers.
const ongoingSeguimientoFetches = new Map();

// Devuelve info de franja y retraso para una entrega
const getEntregaFranjaInfo = (entrega, compararConActual = false) => {
  const franja = entrega.franja_horaria;
  if (!franja || franja === '-') return { tipo: 'sin-franja', retrasoMs: null, franja };
  const [ini, fin] = franja.split('-');
  if (!ini || !fin) return { tipo: 'sin-franja', retrasoMs: null, franja };
  let entregaHH, entregaMM;
  if (compararConActual) {
    // Usar hora actual de Madrid
    const now = new Date();
    entregaHH = now.getHours();
    entregaMM = now.getMinutes();
  } else {
    // Extraer hora y minuto de la entrega (entregas ya realizadas)
    if (typeof entrega.dia_reparto === 'string' && entrega.dia_reparto) {
      const m = entrega.dia_reparto.match(/\b(\d{2}):(\d{2})/);
      if (m) {
        entregaHH = Number(m[1]);
        entregaMM = Number(m[2]);
      }
    }
    if (entregaHH == null || entregaMM == null) {
      const fechaEntrega = parseDateSafe(entrega.dia_reparto);
      if (!fechaEntrega) return { tipo: 'sin-franja', retrasoMs: null, franja };
      entregaHH = fechaEntrega.getHours();
      entregaMM = fechaEntrega.getMinutes();
    }
  }
  const [h, m] = fin.split(':');
  const entregaMin = entregaHH * 60 + entregaMM;
  const finMin = Number(h) * 60 + Number(m);
  if (entregaMin <= finMin) return { tipo: 'en-hora', retrasoMs: 0, franja };
  const retrasoMin = entregaMin - finMin;
  return { tipo: 'retraso', retrasoMs: retrasoMin * 60 * 1000, franja };
};


// Robust parsing: supports RFC 1123 GMT (e.g., "Sat, 04 Oct 2025 10:18:41 GMT") and ISO strings
const parseDateSafe = (s) => {
  if (!s) return null;
  // Try native Date first (handles RFC1123 and ISO in modern browsers)
  let d = new Date(s);
  if (!isNaN(d)) return d;
  // Fallback: try replacing space to T (old ISO-like)
  d = new Date(String(s).replace(" ", "T"));
  if (!isNaN(d)) return d;
  // Last resort: Date.parse number to Date
  const t = Date.parse(String(s));
  if (!Number.isNaN(t)) return new Date(t);
  return null;
};

const fmtH = (d) => {
  if (typeof d === 'string' && /GMT\s*$/.test(d)) {
    // Si viene como RFC1123 con "GMT" (pero sabemos que el valor corresponde a hora local de España),
    // extraemos HH:MM directamente del string para evitar sumar el offset.
    const m = d.match(/\b(\d{2}):(\d{2}):(\d{2})\b/);
    if (m) return `${m[1]}:${m[2]}`;
  }
  const dt = parseDateSafe(d);
  if (!dt) return String(d || "");
  // IMPORTANTE: la hora ya está en local Madrid, no aplicar zona ni Intl ni nada
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const fmtDur = (ms) => {
  if (ms == null) return "-";
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  // Solo horas y minutos, nunca segundos
  if (hh > 0) return `${hh}h ${mm}m`;
  return `${mm}m`;
};

// Group deliveries by dia_recogida (repartos). If dia_recogida changes, start a new group.
const groupByReparto = (inicio, enReparto, entregas, intercambios) => {
  const items = (entregas || []).map(e => ({ ...e, type: 'entrega', t: parseDateSafe(e.dia_reparto) }));
  const swaps = (intercambios || []).map(x => ({ ...x, type: 'swap', t: parseDateSafe(x.dia_cambio) }));
  // Solo entregas para agrupar
  const entregasTimeline = items.filter(x => x.t).sort((a, b) => a.t - b.t);
  // Crear grupos solo con entregas
  const groups = [];
  let current = null;
  for (const it of entregasTimeline) {
    const rec = parseDateSafe(it.dia_recogida);
    if (!current || (current.dia_recogida && rec && rec.getTime() !== current.dia_recogida.getTime())) {
      current = { dia_recogida: rec, dia_recogida_raw: it.dia_recogida, items: [] };
      groups.push(current);
    } else if (!current) {
      current = { dia_recogida: rec, dia_recogida_raw: it.dia_recogida, items: [] };
      groups.push(current);
    }
    current.items.push(it);
  }
  // Asignar swaps al grupo correcto según su hora
  for (const swap of swaps) {
    let assigned = false;
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      // Rango: desde primer entrega del grupo hasta la primera del siguiente (o infinito)
      const t0 = g.items.length > 0 ? parseDateSafe(g.items[0].dia_reparto) : null;
      const t1 = (groups[i + 1] && groups[i + 1].items.length > 0) ? parseDateSafe(groups[i + 1].items[0].dia_reparto) : null;
      if (t0 && swap.t && (!t1 || (swap.t < t1))) {
        if (swap.t >= t0) {
          g.items.push(swap);
          assigned = true;
          break;
        }
      }
    }
    // Si no encaja en ningún grupo, lo ponemos en el primero posterior a su hora, o al último
    if (!assigned) {
      let idx = groups.findIndex(g => g.items.length > 0 && swap.t < parseDateSafe(g.items[0].dia_reparto));
      if (idx === -1) idx = groups.length - 1;
      if (groups[idx]) groups[idx].items.push(swap);
    }
  }
  // Ordenar items dentro de cada grupo por hora
  for (const g of groups) {
    g.items.sort((a, b) => {
      const ta = a.type === 'entrega' ? parseDateSafe(a.dia_reparto) : parseDateSafe(a.dia_cambio);
      const tb = b.type === 'entrega' ? parseDateSafe(b.dia_reparto) : parseDateSafe(b.dia_cambio);
      return ta - tb;
    });
  }
  // compute durations strictly between consecutive deliveries within each group
  for (const g of groups){
    let prevEntregaTime = null;
    let lastEntregaRaw = null;
    let totalBetweenMs = 0;
    let firstEntregaTime = null;
    for (const it of g.items){
      if (it.type === 'entrega'){
        if (!firstEntregaTime){ firstEntregaTime = parseDateSafe(it.dia_reparto); }
        if (prevEntregaTime){
          const d2 = parseDateSafe(it.dia_reparto);
          if (d2){
            const diff = Math.max(0, d2 - prevEntregaTime);
            it.diffFromPrevMs = diff;
            totalBetweenMs += diff;
          }
        }
        prevEntregaTime = parseDateSafe(it.dia_reparto) || prevEntregaTime;
        lastEntregaRaw = it.dia_reparto || lastEntregaRaw;
      }
    }
    g.totalBetweenMs = totalBetweenMs;
    // first leg from start (dia_recogida) to first entrega of the group
    if (g.dia_recogida && firstEntregaTime){
      const diff0 = Math.max(0, firstEntregaTime - g.dia_recogida);
      g.firstLegMs = diff0;
    } else {
      g.firstLegMs = null;
    }
    // end time of reparto = last entrega time in this group
    g.fin = prevEntregaTime || null;
    g.fin_raw = lastEntregaRaw || null;
  }
  // For header count, compute n_pisos_totales per group: number of entregas + pendientes del enReparto con esa dia_recogida
  const enMap = new Map();
  (enReparto||[]).forEach(e=>{
    const key = parseDateSafe(e.dia_recogida)?.getTime() || 0;
    enMap.set(key, (enMap.get(key)||0) + 1);
  });
  for (const g of groups){
    const key = g.dia_recogida?.getTime() || 0;
    const enCount = (enMap.get(key)||0);
    g.enCount = enCount;
    g.n_pisos_totales = (g.items.filter(x=>x.type==='entrega').length) + enCount;
  }
  return groups;
};

const getToday = () => {
  const tzNow = new Date();
  const yyyy = tzNow.getFullYear();
  const mm = String(tzNow.getMonth() + 1).padStart(2, "0");
  const dd = String(tzNow.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const SeguimientoRepartidores = () => {
  const [repartidores, setRepartidores] = useState([]);
  const [repartidor, setRepartidor] = useState(() => {
    if (typeof window !== 'undefined') {
      const q = window.localStorage.getItem('segRep.repartidor') || "";
      return q;
    }
    return "";
  });
  const [dia, setDia] = useState(() => {
    const today = getToday();
    if (typeof window !== 'undefined') {
      const q = window.localStorage.getItem('segRep.dia') || today;
      return q;
    }
    return today;
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [data, setData] = useState({
    started: false,
    inicio: null,
    preparando: [],
    en_reparto: [],
    entregas: [],
    intercambios: [],
  });

  const esHoy = useMemo(() => {
    const tzNow = new Date();
    const yyyy = tzNow.getFullYear();
    const mm = String(tzNow.getMonth() + 1).padStart(2, "0");
    const dd = String(tzNow.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}` === dia;
  }, [dia]);

  // cargar repartidores
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/usuarios/repartidores`);
        const rows = res.data?.data || res.data || [];
        setRepartidores(rows);
      } catch (e) { /* noop */ }
    })();
  }, []);

  // Persistir filtros inmediatamente al cambiar (para que sobrevivan a reload)
  // y limpiar al desmontar (navegación SPA). Esto guarda en localStorage en cada
  // cambio y elimina las claves cuando el componente se desmonta.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('segRep.repartidor', repartidor || "");
      window.localStorage.setItem('segRep.dia', dia || getToday());
    } catch (e) { /* ignore */ }

    const onBeforeUnload = () => {
      try {
        // Keep values across reload (do NOT remove here) — beforeunload runs on reload/close,
        // but we want reload to keep values; however we still remove on unmount below.
        // Intentionally left empty to avoid removing on reload.
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      try {
        window.localStorage.removeItem('segRep.repartidor');
        window.localStorage.removeItem('segRep.dia');
      } catch (e) { /* ignore */ }
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [repartidor, dia]);

  const fetchData = async () => {
    const key = `${repartidor}::${dia}`;
    console.log('[SeguimientoRepartidores] fetchData called', { repartidor, dia, key });
    if (!repartidor || !dia) {
      console.log('[SeguimientoRepartidores] fetchData aborted: missing repartidor or dia');
      return;
    }

    // If there's already an identical fetch in progress, skip launching another.
    if (ongoingSeguimientoFetches.has(key)) {
      console.log('[SeguimientoRepartidores] fetchData skipping because identical fetch in progress', key);
      return;
    }

    ongoingSeguimientoFetches.set(key, true);
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`/api/compras/seguimiento_repartidor`, {
        params: { user_repartidor: repartidor, dia }
      });
      setData(r.data || {});
      console.log('[SeguimientoRepartidores] fetchData success, items counts:', { entregas: (r.data?.entregas||[]).length, en_reparto: (r.data?.en_reparto||[]).length });
    } catch (e) {
      setErr("Error cargando seguimiento");
      console.error('[SeguimientoRepartidores] fetchData error', e);
      setData({ started:false, inicio:null, preparando:[], en_reparto:[], entregas:[], intercambios:[] });
    } finally {
      ongoingSeguimientoFetches.delete(key);
      setLoading(false);
    }
  };

  // Actualizar sin perder la posición de scroll
  const handleRefresh = async () => {
    console.log('[SeguimientoRepartidores] manual refresh triggered');
    const y = typeof window !== 'undefined' ? window.scrollY : 0;
    await fetchData();
    if (typeof window !== 'undefined') {
      // Use RAF + tiny timeout to ensure DOM updates have been painted before scrolling
      window.requestAnimationFrame(() => setTimeout(() => window.scrollTo(0, y), 0));
    }
  };

  useEffect(() => {
    // Preserve scroll position when auto-refreshing data due to filter changes
    const run = async () => {
      console.log('[SeguimientoRepartidores] auto-refresh run for', { repartidor, dia });
      const y = (typeof window !== 'undefined') ? window.scrollY : 0;
      await fetchData();
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => setTimeout(() => window.scrollTo(0, y), 0));
      }
      console.log('[SeguimientoRepartidores] auto-refresh finished');
    };
    run();
    // eslint-disable-next-line
  }, [repartidor, dia]);

  const iniciarPedido = async () => {
    try {
      await axios.post(`/api/compras/iniciar_reparto`, { user_repartidor: repartidor });
      await fetchData();
    } catch (e) { alert('No se pudo iniciar el reparto'); }
  };

  // Preparando sólo si NO está en reparto y si es hoy
  const mostrarPreparando = esHoy && !data.started && Array.isArray(data.preparando) && data.preparando.length > 0;

  // Duraciones por entrega y total
  const grupos = useMemo(() => groupByReparto(data.inicio, data.en_reparto||[], data.entregas||[], data.intercambios||[]), [data.inicio, data.en_reparto, data.entregas, data.intercambios]);

  // Desplegables: estado por grupo y control de apertura/cierre
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    setExpanded(prev => {
      const next = { ...prev };
      (grupos || []).forEach((g, gi) => {
        if (next[gi] === undefined) next[gi] = (g.enCount || 0) > 0; // abiertos si en reparto
      });
      return next;
    });
  }, [grupos]);
  const [prepOpen, setPrepOpen] = useState(true);
  useEffect(() => {
    if (mostrarPreparando) setPrepOpen(true);
  }, [mostrarPreparando]);

  return (
    <div style={styles.container}>
      <BotonVolver fallback="/admin" />
      <h1 style={styles.title}>Seguimiento Repartidores</h1>

      <div style={styles.filtersCard}>
        <div style={styles.filters}>
          <div style={styles.filter}> 
            <label style={styles.label}>Repartidor</label>
            <select style={styles.inputCtl} value={repartidor} onChange={(e) => setRepartidor(e.target.value)}>
              <option value="">-- Selecciona --</option>
              {repartidores.map((r) => (
                <option key={r.user} value={r.user}>{r.user}</option>
              ))}
            </select>
          </div>
          <div style={styles.filter}>
            <label style={styles.label}>Día</label>
            <input style={styles.inputCtl} type="date" value={dia} onChange={(e) => setDia(e.target.value)} />
          </div>
        </div>
      </div>

      {repartidor && dia && (
        <div style={styles.subheaderCard}>
          <div style={styles.subheaderBig}>Seguimiento compras</div>
          <div style={styles.subheaderLine}><b style={{ fontSize: 22 }}>{repartidor}</b> — <span style={{ fontSize: 18 }}>{dia}</span></div>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 16, color: '#334155', background: '#fef3c7', padding: '8px 12px', borderRadius: 8, border: '1px solid #f59e0b' }}>
              <span style={{ fontWeight: 600 }}>Entregas totales:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{(data.entregas || []).length + (data.en_reparto || []).length}</span>
            </div>
            <div style={{ fontSize: 16, color: '#334155', background: '#dbeafe', padding: '8px 12px', borderRadius: 8, border: '1px solid #3b82f6' }}>
              <span style={{ fontWeight: 600 }}>Tiempo total:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{fmtDur(grupos.reduce((sum, g) => sum + (g.firstLegMs || 0) + (g.totalBetweenMs || 0), 0))}</span>
            </div>
            <div style={{ fontSize: 16, color: '#334155', background: '#dcfce7', padding: '8px 12px', borderRadius: 8, border: '1px solid #10b981' }}>
              <span style={{ fontWeight: 600 }}>Tiempo medio:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{(() => {
                const totalCompras = (data.entregas || []).length + (data.en_reparto || []).length;
                const totalTiempo = grupos.reduce((sum, g) => sum + (g.firstLegMs || 0) + (g.totalBetweenMs || 0), 0);
                return totalCompras > 0 ? fmtDur(totalTiempo / totalCompras) : 'N/A';
              })()}</span>
            </div>
          </div>
        </div>
      )}

      {loading && <div>Cargando…</div>}
      {err && <div style={styles.error}>{err}</div>}

      {!loading && repartidor && dia && (
        <div style={styles.timeline}>
          {/* 1. Mensaje si vacío */}
          {!data.started && (!data.entregas?.length) && (!data.en_reparto?.length) && (!data.preparando?.length) && (
            <div style={styles.block}>
              <h2>Sin actividad</h2>
              <p>No ha empezado reparto {repartidor} el día {dia}.</p>
            </div>
          )}

          {/* 2.1 Preparando como reparto desplegable */}
          {mostrarPreparando && (
            <div style={styles.block}>
              <details open={prepOpen} onToggle={(e)=>setPrepOpen(e.target.open)}>
                <summary>
                  <div style={{...styles.summaryBox, ...styles.summaryPreparando}}>
                    <div style={styles.repartoHeader}>
                      <div style={styles.repartoTitle}>PREPARANDO PEDIDO</div>
                      <div style={styles.repartoTotal}>Total: {data.preparando.length}</div>
                    </div>
                  </div>
                </summary>
                <div style={{ marginTop: 10 }}>
                  <div style={styles.badgeList}>
                    {data.preparando.map((p) => {
                      const franjaInfo = getEntregaFranjaInfo(p, true);
                      let bg = '#e0f2fe', color='#0369a1', border='#bae6fd'; // azul por defecto (sin franja)
                      if (franjaInfo.tipo === 'retraso') { bg = '#fee2e2'; color='#b91c1c'; border='#fecaca'; }
                      else if (franjaInfo.tipo === 'en-hora' && p.franja_horaria && p.franja_horaria !== '-') {
                        // Quedan <=40min para cierre
                        const [ini, fin] = p.franja_horaria.split('-');
                        const now = new Date();
                        const [fh, fm] = fin.split(':');
                        const finMin = Number(fh)*60+Number(fm);
                        const nowMin = now.getHours()*60+now.getMinutes();
                        if (finMin - nowMin <= 40 && finMin - nowMin > 0) {
                          bg = '#fef9c3'; color='#b45309'; border='#fde68a'; // naranja
                        } else {
                          bg = '#dcfce7'; color='#15803d'; border='#bbf7d0'; // verde
                        }
                      }
                      return (
                        <span key={p.id} style={{
                          ...styles.badge,
                          background:bg, color, borderColor:border, border:'1.5px solid '+border, fontWeight:700, fontSize:16
                        }}>
                          {p.abreviatura_piso} {p.n_bolsas!=null? `🛍️ ${p.n_bolsas}`:''} {p.franja_horaria ? <span style={{color:'#64748b', fontWeight:600, fontSize:13}}>[{p.franja_horaria}]</span> : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </details>
            </div>
          )}

          {/* 2.2 Listado de pisos en reparto (sin contenedor de 'En reparto') */}
          {data.started && (data.en_reparto||[]).length > 0 && (
            <div style={styles.enRepartoListWrap}>
              <div style={styles.enRepartoListTitle}>Pisos en reparto</div>
              <ul style={styles.enRepartoList}>
                {data.en_reparto.map((p) => {
                  const franjaInfo = getEntregaFranjaInfo(p, true);
                  let bg = '#e0f2fe', color='#0369a1', border='#bae6fd'; // azul por defecto (sin franja)
                  if (franjaInfo.tipo === 'retraso') { bg = '#fee2e2'; color='#b91c1c'; border='#fecaca'; }
                  else if (franjaInfo.tipo === 'en-hora' && p.franja_horaria && p.franja_horaria !== '-') {
                    // Quedan <=40min para cierre
                    const [ini, fin] = p.franja_horaria.split('-');
                    const now = new Date();
                    const [fh, fm] = fin.split(':');
                    const finMin = Number(fh)*60+Number(fm);
                    const nowMin = now.getHours()*60+now.getMinutes();
                    if (finMin - nowMin <= 40 && finMin - nowMin > 0) {
                      bg = '#fef9c3'; color='#b45309'; border='#fde68a'; 
                    } else {
                      bg = '#dcfce7'; color='#15803d'; border='#bbf7d0';
                    }
                  }
                  return (
                    <li key={p.id} style={{
                      ...styles.enRepartoItem,
                      background:bg, color, border:'1.5px solid '+border, borderRadius:12, padding:'6px 10px', fontWeight:700, fontSize:18, marginBottom:2
                    }}>
                      <span style={styles.enRepartoPiso}><b>{p.abreviatura_piso}</b></span>
                      {p.n_bolsas!=null && <span style={styles.enRepartoBolsas}>🛍️ {p.n_bolsas}</span>}
                      {p.franja_horaria && <span style={{color:'#64748b', fontWeight:600, fontSize:13, marginLeft:6}}>[{p.franja_horaria}]</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* 2.3 Entregas con duraciones y total */}
          {(grupos||[]).length > 0 && (
            <div style={styles.block}>
              <h2 style={styles.sectionTitle}>Entregas del día</h2>
              {/* Resumen de totales del día */}
              <div style={styles.countsRow}>
                <span style={{...styles.countBadge, background:'#eafaf1', borderColor:'#34d399', color:'#065f46'}}>Entregados: {(data.entregas||[]).filter(e=> e.estado==='entregado').length}</span>
                <span style={{...styles.countBadge, background:'#fdecec', borderColor:'#f87171', color:'#991b1b'}}>En la puerta: {(data.entregas||[]).filter(e=> e.estado==='en la puerta').length}</span>
                <span style={{...styles.countBadge, background:'#eef2ff', borderColor:'#93c5fd', color:'#1e3a8a'}}>En reparto: {(data.en_reparto||[]).length}</span>
                <span style={{...styles.countBadge, background:'#fff7ed', borderColor:'#f59e0b', color:'#92400e'}}>➡️ Out: {(data.intercambios||[]).filter(x=> x.direccion==='out').length}</span>
                <span style={{...styles.countBadge, background:'#fff7ed', borderColor:'#f59e0b', color:'#92400e'}}>⬅️ In: {(data.intercambios||[]).filter(x=> x.direccion==='in').length}</span>
              </div>
              {(grupos).map((g, gi) => (
                <div key={gi} style={{ marginBottom: 18 }}>
                  <details open={!!expanded[gi]} onToggle={(e)=> setExpanded(prev=>({ ...prev, [gi]: e.target.open }))}>
                    <summary>
                      <div style={{...styles.summaryBox, ...(g.enCount > 0 ? styles.summaryEnReparto : styles.summaryFinalizado)}}>
                        <div style={styles.repartoHeader}>
                          <div style={styles.repartoTitle}>Reparto {gi+1} {g.dia_recogida_raw && g.enCount > 0 && <span style={styles.repartoTime}>({fmtH(g.dia_recogida_raw)})</span>}</div>
                          <div style={styles.repartoTotal}>Total: {g.n_pisos_totales}</div>
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {g.enCount > 0 ? (
                            <span style={{...styles.statusTag, ...styles.enRepartoTag}}>(EN REPARTO)</span>
                          ) : (
                            <span style={{...styles.statusTag, ...styles.finalizadoTag}}>(REPARTO FINALIZADO)</span>
                          )}
                        </div>
                        {g.enCount === 0 && (
                          <div style={styles.timeInfoRow}>
                            {g.dia_recogida_raw && (<span style={styles.timeChip}>Inicio: {fmtH(g.dia_recogida_raw)}</span>)}
                            {g.fin_raw && (<span style={styles.timeChip}>Fin: {fmtH(g.fin_raw)}</span>)}
                          </div>
                        )}
                      </div>
                    </summary>
                    <div style={styles.timelineWrap}>
                      <div style={styles.verticalLine} />
                      {(() => {
                      const elements = [];
                      let prevEntrega = null;
                      g.items.forEach((it, idx) => {
                        if (it.type === 'entrega'){
                          const franjaInfo = getEntregaFranjaInfo(it);
                          let dotColor = '#0ea5e9', borderColor = '#0369a1';
                          if (franjaInfo.tipo === 'en-hora') { dotColor = '#22c55e'; borderColor = '#15803d'; }
                          else if (franjaInfo.tipo === 'retraso') { dotColor = '#ef4444'; borderColor = '#b91c1c'; }
                          else if (franjaInfo.tipo === 'sin-franja') { dotColor = '#0ea5e9'; borderColor = '#0369a1'; }
                          if (prevEntrega){
                            elements.push(
                              <div key={`dur-${idx}-${it.id||it.id_compra||idx}`} style={styles.betweenRow}>
                                <div style={styles.durationBetween}>⏱ {fmtDur(it.diffFromPrevMs)}</div>
                              </div>
                            );
                          } else if (g.firstLegMs != null){
                            elements.push(
                              <div key={`dur0-${idx}-${it.id||it.id_compra||idx}`} style={styles.betweenRow}>
                                <div style={styles.durationBetween}>⏱ {fmtDur(g.firstLegMs)}</div>
                              </div>
                            );
                          }
                          elements.push(
                            <div key={`it-${idx}-${it.id||it.id_compra||idx}`} style={{...styles.timelineItem, display:'flex', alignItems:'center', marginLeft:-32}}>
                              {/* Contenedor de ancho fijo para retraso */}
                              <div style={{width:70, textAlign:'right', marginRight:0, flexShrink:0}}>
                                {franjaInfo.tipo === 'retraso' && (
                                  <span style={{ color: '#b91c1c', fontWeight: 800, fontSize:15 }}>{fmtDur(franjaInfo.retrasoMs)}</span>
                                )}
                              </div>
                              {/* Punto de color SIEMPRE alineado en columna fija */}
                              <div style={{
                                ...styles.dot,
                                background: dotColor,
                                borderColor: borderColor,
                                margin: '0 18px',
                                position: 'relative',
                                left: 0,
                                flexShrink: 0
                              }} />
                              {/* Hora de entrega justo a la derecha del punto */}
                              <span style={{...styles.time, marginRight: 10}}>{fmtH(it.dia_reparto)}</span>
                              {/* Contenido principal */}
                              <div style={styles.itemContent}>
                                <div style={styles.itemLineRow}>
                                  {/* ...resto del contenido... */}
                                  <span style={styles.piso}>
                                    <b>{it.abreviatura_piso}</b>
                                    {it.n_bolsas!=null && (
                                      <div style={{fontWeight:600, fontSize:15, color:'#155e75', marginTop:0}}>
                                        🛍️ {it.n_bolsas}
                                      </div>
                                    )}
                                    <span style={{color:'#64748b', fontWeight:700, fontSize:17, letterSpacing:'0.5px'}}>{it.franja_horaria ? `[${it.franja_horaria}]` : ''}</span>
                                  </span>
                                  <span style={{...styles.estadoChip, ...(it.estado === 'entregado' ? styles.entregadoChip : styles.enPuertaChip)}}>{it.estado}</span>
                                </div>
                              </div>
                            </div>
                          );
                          prevEntrega = it;
                        } else {
                          elements.push(
                            <div key={`swap-${idx}-${it.id_compra||idx}`} style={{...styles.timelineItem, display:'flex', alignItems:'center', marginLeft:-32}}>
                              {/* Contenedor de ancho fijo para retraso (vacío para intercambios) */}
                              <div style={{width:70, textAlign:'right', marginRight:0, flexShrink:0}}></div>
                              {/* Punto de color alineado igual que entregas */}
                              <div style={{
                                ...styles.dot,
                                background: '#d97706',
                                borderColor: '#92400e',
                                margin: '0 18px',
                                position: 'relative',
                                left: 0,
                                flexShrink: 0
                              }} />
                              {/* Hora de intercambio justo a la derecha del punto */}
                              <span style={{...styles.time, marginRight: 10}}>{fmtH(it.dia_cambio)}</span>
                              {/* Contenido principal: destino y debajo info piso/bolsas/franja */}
                              <div style={styles.itemContent}>
                                <div style={{fontWeight:700, color:'#d97706', fontSize:16, marginBottom:2}}>
                                  {it.direccion==='out' ? '➡️ a ' : '⬅️ de '}<strong>{it.otro_user}</strong>
                                </div>
                                <div style={{color:'#1f2937', fontWeight:600, fontSize:15, marginBottom:0}}>
                                  {it.abreviatura_piso ? `${it.abreviatura_piso} `: ''}{it.id_compra ? `(${it.id_compra}) ` : ''}{it.n_bolsas!=null? `🛍️ ${it.n_bolsas}`:''}
                                  {it.franja_horaria ? <span style={{color:'#64748b', fontWeight:700, fontSize:17, letterSpacing:'0.5px', marginLeft:6}}>[{it.franja_horaria}]</span> : ''}
                                </div>
                              </div>
                            </div>
                          );
                        }
                      });
                      return elements;
                    })()}
                    </div>
                    <div style={{ marginTop: 8, fontWeight: 700 }}>
                      Tiempo total entre entregas: {fmtDur((g.firstLegMs || 0) + (g.totalBetweenMs || 0))}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          )}

          {/* Intercambios eliminados como sección independiente; ya aparecen en el timeline */}

          {/* Terminado */}
          {data.started && (data.en_reparto||[]).length === 0 && (
            <div style={styles.block}>
              <h2 style={{ color: 'green' }}>REPARTO TERMINADO</h2>
            </div>
          )}
        </div>
      )}
      {/* Botón fijo abajo a la derecha */}
      <button style={styles.fabRefresh} onClick={handleRefresh} title="Actualizar">⟳ Actualizar</button>
    </div>
  );
};

const styles = {
  container: { padding: 20, maxWidth: 900, margin: "0 auto" },
  title: { marginBottom: 20 },
  filtersCard: { border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#f0f9ff', marginBottom:16 },
  filters: { display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" },
  filter: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontWeight:700, fontSize:13, color:'#334155' },
  inputCtl: { padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:8, fontSize:14, outline:'none' },
  refresh: { padding: "8px 12px" },
  primary: { padding: "10px 14px", background: "#007bff", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
  subheader: { marginTop: 12, fontSize: 18, color:'#111827', fontWeight:800 },
  subheaderCard: { border:'1px solid #e5e7eb', borderRadius:12, padding:'14px 16px', background:'#e0f2fe', marginBottom:16, boxShadow:'0 1px 2px rgba(16,24,40,0.04)' },
  subheaderBig: { fontSize: 26, lineHeight: '28px', fontWeight: 900, color:'#0f172a', marginBottom: 6 },
  subheaderLine: { fontSize: 18, color:'#334155', fontWeight:700 },
  error: { color: "red", marginBottom: 10 },
  timeline: { display: "grid", gap: 16 },
  block: { border: "1px solid #ddd", borderRadius: 8, padding: 12, background: '#fafafa' },
  sectionTitle: { margin:0, marginBottom:8, fontSize:24 },
  badgeList: { marginTop: 8, color: "#333", display: 'flex', flexWrap: 'wrap', gap: 6 },
  badge: { border: '1px solid #ccc', borderRadius: 16, padding: '4px 10px', background: '#f7f7f7' },
  ul: { margin: 0, paddingLeft: 16 },
  li: { marginBottom: 6 },
  timelineWrap: { position: 'relative', paddingLeft: 34, paddingTop: 8 },
  verticalLine: { position: 'absolute', top: 0, bottom: 0, left: 16, width: 3, background: 'linear-gradient(#94a3b8, #cbd5e1)' },
  timelineItem: { position: 'relative', marginBottom: 14 },
  dot: { position: 'absolute', left: 11, top: 4, width: 12, height: 12, borderRadius: '50%', background: '#0ea5e9', border: '2px solid #0369a1', boxShadow:'0 0 0 3px rgba(14,165,233,0.15)' },
  itemContent: { marginLeft: 16, paddingLeft: 16 },
  itemHeader: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  itemLineRow: { display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' },
  time: { fontWeight: 800, color: '#0f172a' },
  piso: { color: '#1f2937' },
  estado: { fontWeight: 700 },
  estadoChip: { fontWeight: 800, fontSize: 12, padding:'2px 8px', borderRadius: 999, border: '1px solid transparent' },
  entregadoChip: { background:'#eafaf1', color:'#065f46', borderColor:'#34d399' },
  enPuertaChip: { background:'#fdecec', color:'#991b1b', borderColor:'#f87171' },
  durationBetween: { marginTop: 6, color: '#555', background: '#f1f5f9', display: 'inline-block', padding: '2px 8px', borderRadius: 12, border: '1px solid #e2e8f0' },
  durationChip: { marginBottom: 8, color: '#555', background: '#ecfeff', display: 'inline-block', padding: '4px 10px', borderRadius: 12, border: '1px solid #a5f3fc' },
  startRow: { display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', margin:'4px 0 8px' },
  startBadge: { background:'#eef2ff', color:'#3730a3', border:'1px solid #c7d2fe', borderRadius:16, padding:'6px 12px', fontWeight:900, fontSize:16 },
  quedan: { background:'#ecfeff', color:'#155e75', border:'1px solid #a5f3fc', borderRadius:16, padding:'6px 12px', fontWeight:900, fontSize:16 },
  countsRow: { display:'flex', gap:8, flexWrap:'wrap', margin:'6px 0 12px' },
  countBadge: { border:'1px solid', borderRadius:14, padding:'6px 12px', fontWeight:800, fontSize:15 },
  repartoHeader: { display:'flex', alignItems:'center', gap:12, marginBottom:10 },
  repartoTitle: { fontWeight:900, fontSize:24 },
  repartoTime: { background:'#fff7ed', color:'#92400e', border:'1px solid #fdba74', padding:'6px 12px', borderRadius:12, fontSize:18, marginLeft:8 },
  repartoTotal: { marginLeft:'auto', fontWeight:900, fontSize:16, background:'#ecfeff', color:'#155e75', border:'1px solid #a5f3fc', borderRadius:12, padding:'4px 10px' },
  betweenRow: { position:'relative', marginLeft: 16, paddingLeft: 16, marginTop: 6, marginBottom: 16, display:'flex', alignItems:'center', minHeight: 20 },
  statusTag: { display:'inline-block', padding:'4px 12px', borderRadius: 999, fontWeight:900, fontSize:14, border:'1px solid transparent' },
  enRepartoTag: { background:'#eef2ff', color:'#3730a3', borderColor:'#c7d2fe' },
  finalizadoTag: { background:'#eafaf1', color:'#065f46', borderColor:'#34d399' },
  preparandoTag: { background:'#fff7ed', color:'#92400e', borderColor:'#fdba74' },
  // Lista vertical de pisos en reparto
  enRepartoListWrap: { border:'1px dashed #c7d2fe', background:'#eef2ff', borderRadius:12, padding:'10px 12px' },
  enRepartoListTitle: { fontWeight:900, fontSize:22, color:'#1e3a8a', marginBottom:10 },
  enRepartoList: { listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:8 },
  enRepartoItem: { display:'flex', alignItems:'center', gap:12, fontSize:18 },
  enRepartoPiso: { color:'#0f172a' },
  enRepartoBolsas: { color:'#155e75', fontWeight:800 },
  summaryBox: { padding:'10px 12px', borderRadius:12, border:'1px solid', display:'block' },
  summaryEnReparto: { background:'#eef2ff', borderColor:'#c7d2fe' },
  summaryFinalizado: { background:'#eafaf1', borderColor:'#34d399' },
  summaryPreparando: { background:'#fff7ed', borderColor:'#fdba74' },
  timeInfoRow: { marginTop: 6, display:'flex', gap:8, flexWrap:'wrap' },
  timeChip: { background:'#f1f5f9', color:'#0f172a', border:'1px solid #e2e8f0', borderRadius:12, padding:'4px 10px', fontWeight:800 },
  fabRefresh: { position:'fixed', right:16, bottom:16, zIndex:1000, background:'#007bff', color:'#fff', border:'none', borderRadius:999, padding:'12px 18px', fontSize:16, boxShadow:'0 8px 16px rgba(0,0,0,0.15)', cursor:'pointer', fontWeight:900 }
};

export default SeguimientoRepartidores;
